import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PaymentModal from '../components/PaymentModal';
import { simulateCampaign, createCampaignFromSimulation, finalizeCampaign, deleteCampaign, getSavedCreators, getCampaignDetails, getCampaignAllocation, recoverPayment } from '../api/api';
import { MONITORING_DURATION_OPTIONS, DEFAULT_MONITORING_WEEKS, FIXED_PAYMENT_PERCENTAGE, PERFORMANCE_PAYMENT_PERCENTAGE } from '../config/paymentConfig';
import { IS_PRE_LAUNCH } from '../config/launchMode';
import './CreateCampaign.css';

// localStorage key for persisting campaign creation progress
const CAMPAIGN_STORAGE_KEY = 'milan_campaign_draft';

/** Smallest whole-rupee price allowed per creator (backend still enforces -20% / +50% vs recommended). */
const MIN_CREATOR_PRICE_INR = 1;

function normalizeCreatorPriceInr(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n)) return MIN_CREATOR_PRICE_INR;
  return Math.max(MIN_CREATOR_PRICE_INR, Math.round(n));
}

function CreateCampaign() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const brandId = localStorage.getItem('userId');
  const errorRef = useRef(null);
  const topRef = useRef(null);
  
  // Check for campaign ID in URL (for editing/continuing drafts)
  const urlCampaignId = searchParams.get('id');
  const urlStep = searchParams.get('step');
  
  // Track if we've restored from localStorage (to avoid overwriting)
  const [hasRestoredFromStorage, setHasRestoredFromStorage] = useState(false);
  
  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  
  // Form state with extended parameters
  const [campaignData, setCampaignData] = useState({
    campaign_name: '',
    objective: 'Awareness',
    content_types: [],
    niches: [],
    languages: ['English', 'Hindi'],
    target_cities: [],
    target_country: 'India',
    min_followers: 5000,
    max_followers: 500000,
    min_engagement_rate: 1.0,
    max_engagement_rate: 15.0,
    min_budget: 50000,
    max_budget: 200000,
    delivery_days: 14,
    creator_count: 5,
    verified_only: false,
    gender: 'any',
    creator_tier: 'any',
    has_contact_info: false,
    active_last_days: 30,
    avg_likes_min: 0,
    avg_comments_min: 0,
    monitoring_weeks: 2,
    // Content Brief Fields
    content_brief: '',
    content_script: '',
    dos_and_donts: '',
    hashtags: '',
    reference_links: '',
    key_messages: '',
    cta_requirements: ''
  });
  
  // AI Recommendations
  const [aiResults, setAiResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  
  // Persistent selections across simulations
  const [primaryCreators, setPrimaryCreators] = useState([]);
  const [backupCreators, setBackupCreators] = useState([]);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [campaignId, setCampaignId] = useState(null);
  const [showParamsPanel, setShowParamsPanel] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPriceEditModal, setShowPriceEditModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  
  // CRM Creators (Saved Only)
  const [crmCreators, setCrmCreators] = useState([]);
  const [showCrmPanel, setShowCrmPanel] = useState(false);
  const [loadingCrm, setLoadingCrm] = useState(false);
  const [crmSearchTerm, setCrmSearchTerm] = useState('');
  const [loadingDraft, setLoadingDraft] = useState(false);

  // Tracks whether this draft has a Razorpay payment that was captured but
  // never finalized (e.g. user refreshed during /verify-payment). When set,
  // we show a "Send invitations now" CTA instead of the regular pay button.
  // Shape: { amount, razorpay_order_id, razorpay_payment_id, finalizing,
  // error } | null.
  const [recoverableRazorpay, setRecoverableRazorpay] = useState(null);

  // Load draft campaign if ID is provided in URL
  useEffect(() => {
    const loadDraftCampaign = async () => {
      if (!urlCampaignId) return;
      
      setLoadingDraft(true);
      try {
        // Load campaign details
        const detailsResponse = await getCampaignDetails(urlCampaignId);
        const campaign = detailsResponse.data.campaign;
        
        if (campaign) {
          // Set campaign ID
          setCampaignId(urlCampaignId);
          
          // Populate campaign data from saved campaign
          setCampaignData(prev => ({
            ...prev,
            campaign_name: campaign.campaign_name || campaign.name || '',
            objective: campaign.objective || 'Awareness',
            content_types: campaign.content_types || [],
            niches: campaign.niche || campaign.niches || [],
            languages: campaign.language || campaign.languages || ['English', 'Hindi'],
            target_cities: campaign.target_cities || [],
            target_country: campaign.target_country || 'India',
            min_followers: campaign.follower_range_min || campaign.min_followers || 5000,
            max_followers: campaign.follower_range_max || campaign.max_followers || 500000,
            min_engagement_rate: campaign.min_engagement_rate || 1.0,
            max_engagement_rate: campaign.max_engagement_rate || 15.0,
            min_budget: campaign.budget_min || campaign.min_budget || 50000,
            max_budget: campaign.budget_max || campaign.max_budget || 200000,
            delivery_days: campaign.deadline_days || campaign.delivery_days || 14,
            creator_count: campaign.creator_count_expected || campaign.creator_count || 5,
            verified_only: campaign.verified_only || false,
            gender: campaign.gender || 'any',
            creator_tier: campaign.creator_tier || 'any',
            has_contact_info: campaign.has_contact_info || false,
            active_last_days: campaign.active_last_days || 30,
            avg_likes_min: campaign.avg_likes_min || 0,
            avg_comments_min: campaign.avg_comments_min || 0,
            content_brief: campaign.content_brief || '',
            content_script: campaign.content_script || '',
            dos_and_donts: campaign.dos_and_donts || '',
            hashtags: Array.isArray(campaign.hashtags) ? campaign.hashtags.join(', ') : campaign.hashtags || '',
            reference_links: Array.isArray(campaign.reference_links) ? campaign.reference_links.join(', ') : campaign.reference_links || '',
            key_messages: Array.isArray(campaign.key_messages) ? campaign.key_messages.join(', ') : campaign.key_messages || '',
            cta_requirements: campaign.cta_requirements || ''
          }));
          
          // Try to load allocation (primary/backup creators)
          try {
            const allocationResponse = await getCampaignAllocation(urlCampaignId);
            const allocation = allocationResponse.data;
            
            if (allocation) {
              // Load primary creators
              if (allocation.primary_creators && allocation.primary_creators.length > 0) {
                setPrimaryCreators(allocation.primary_creators.map(c => ({
                  id: c.id || c._id,
                  name: c.name,
                  username: c.username || c.instagram_username,
                  followers: c.followers || c.instagram_followers_count,
                  engagement: c.engagement || c.engagement_rate,
                  pricing: c.pricing || { suggested_price: c.allocated_base_price || 0 },
                  profile_picture_url: c.profile_picture_url
                })));
              }
              
              // Load backup creators
              if (allocation.backup_creators && allocation.backup_creators.length > 0) {
                setBackupCreators(allocation.backup_creators.map(c => ({
                  id: c.id || c._id,
                  name: c.name,
                  username: c.username || c.instagram_username,
                  followers: c.followers || c.instagram_followers_count,
                  engagement: c.engagement || c.engagement_rate,
                  pricing: c.pricing || { suggested_price: c.allocated_base_price || 0 },
                  profile_picture_url: c.profile_picture_url
                })));
              }
              
              // If we have creators selected, mark as searched
              if (allocation.primary_creators?.length > 0 || allocation.backup_creators?.length > 0) {
                setHasSearched(true);
              }
            }
          } catch (allocErr) {
            console.log('No allocation found for this campaign');
          }

          // Recovery: ask the backend whether a payment was captured for this
          // draft but never finalized. The recover-payment endpoint is
          // idempotent — safe to call on every draft load.
          let foundRecoverable = false;
          if (campaign.status === 'draft') {
            try {
              const recRes = await recoverPayment(urlCampaignId);
              if (recRes?.data?.ready_to_finalize) {
                foundRecoverable = true;
                setRecoverableRazorpay({
                  amount: recRes.data.amount,
                  razorpay_order_id: recRes.data.razorpay_order_id,
                  razorpay_payment_id: recRes.data.razorpay_payment_id,
                  recovered: !!recRes.data.recovered,
                });
              }
            } catch (recErr) {
              // Recovery is best-effort; don't block the draft from loading.
              console.warn('recover-payment check failed:', recErr?.message || recErr);
            }
          }

          // Set the step based on URL param or campaign state
          const stepParam = parseInt(urlStep) || 1;
          if (foundRecoverable) {
            // Jump straight to step 3 so the recovery CTA is visible
            // alongside the creator/payment summary.
            setCurrentStep(3);
          } else if (stepParam >= 1 && stepParam <= 3) {
            setCurrentStep(stepParam);
          } else if (campaign.status === 'draft') {
            // If draft with allocations, go to step 2 or 3
            if (detailsResponse.data.allocation) {
              setCurrentStep(2);
            }
          }
        }
      } catch (err) {
        console.error('Error loading draft campaign:', err);
        setError('Failed to load campaign. It may have been deleted.');
      } finally {
        setLoadingDraft(false);
      }
    };
    
    loadDraftCampaign();
  }, [urlCampaignId, urlStep]);

  // Auto-scroll to top when error occurs
  useEffect(() => {
    if (error) {
      if (errorRef.current) {
        errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (topRef.current) {
        topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }, [error]);

  // Restore state from localStorage on mount (only if not loading a specific campaign from URL)
  useEffect(() => {
    if (urlCampaignId) return; // Skip if loading a specific campaign from URL
    
    try {
      const savedDraft = localStorage.getItem(CAMPAIGN_STORAGE_KEY);
      if (savedDraft) {
        const parsed = JSON.parse(savedDraft);
        
        // Restore campaign data
        if (parsed.campaignData) {
          setCampaignData(parsed.campaignData);
        }
        
        // Restore creators
        if (parsed.primaryCreators && parsed.primaryCreators.length > 0) {
          setPrimaryCreators(parsed.primaryCreators);
        }
        if (parsed.backupCreators && parsed.backupCreators.length > 0) {
          setBackupCreators(parsed.backupCreators);
        }
        
        // Restore AI results
        if (parsed.aiResults && parsed.aiResults.length > 0) {
          setAiResults(parsed.aiResults);
          setHasSearched(true);
        }
        
        // Restore step from URL param first, then localStorage
        const stepFromUrl = parseInt(searchParams.get('step')) || 0;
        const savedStep = parsed.currentStep || 1;
        
        // Only restore to steps 1-3 (not success page)
        if (stepFromUrl >= 1 && stepFromUrl <= 3) {
          setCurrentStep(stepFromUrl);
        } else if (savedStep >= 1 && savedStep <= 3) {
          setCurrentStep(savedStep);
          // Update URL to match
          setSearchParams({ step: savedStep.toString() }, { replace: true });
        }
        
        // Restore campaign ID if it was saved
        if (parsed.campaignId) {
          setCampaignId(parsed.campaignId);
        }
        
        console.log('Restored campaign draft from localStorage, step:', stepFromUrl || savedStep);
      }
    } catch (err) {
      console.error('Error restoring campaign draft:', err);
      localStorage.removeItem(CAMPAIGN_STORAGE_KEY);
    }
    
    setHasRestoredFromStorage(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save state to localStorage whenever important data changes
  useEffect(() => {
    // Don't save if we haven't finished restoring yet
    if (!hasRestoredFromStorage) return;
    
    // Don't save if loading a specific campaign from URL
    if (urlCampaignId) return;
    
    // Don't save if on success page (step 4) - clear instead
    if (currentStep === 4) {
      localStorage.removeItem(CAMPAIGN_STORAGE_KEY);
      return;
    }
    
    // Save current state to localStorage
    const dataToSave = {
      campaignData,
      primaryCreators,
      backupCreators,
      aiResults,
      currentStep,
      campaignId,
      savedAt: new Date().toISOString()
    };
    
    localStorage.setItem(CAMPAIGN_STORAGE_KEY, JSON.stringify(dataToSave));
  }, [campaignData, primaryCreators, backupCreators, aiResults, currentStep, campaignId, hasRestoredFromStorage, urlCampaignId]);

  // Update URL params when step changes (for shareable URLs and refresh support)
  useEffect(() => {
    if (!hasRestoredFromStorage) return;
    if (urlCampaignId) return; // Don't update URL if loading specific campaign
    
    // Only update URL for steps 1-3, clear for step 4 (success)
    if (currentStep >= 1 && currentStep <= 3) {
      const currentUrlStep = searchParams.get('step');
      if (currentUrlStep !== currentStep.toString()) {
        setSearchParams({ step: currentStep.toString() }, { replace: true });
      }
    } else if (currentStep === 4) {
      // Clear step param on success
      if (searchParams.has('step')) {
        searchParams.delete('step');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [currentStep, hasRestoredFromStorage, urlCampaignId, searchParams, setSearchParams]);

  // Clear saved progress and start fresh
  const handleStartFresh = () => {
    if (window.confirm('This will clear all your current campaign progress. Are you sure?')) {
      localStorage.removeItem(CAMPAIGN_STORAGE_KEY);
      
      // Reset all state
      setCampaignData({
        campaign_name: '',
        objective: 'Awareness',
        content_types: [],
        niches: [],
        languages: ['English', 'Hindi'],
        target_cities: [],
        target_country: 'India',
        min_followers: 5000,
        max_followers: 500000,
        min_engagement_rate: 1.0,
        max_engagement_rate: 15.0,
        min_budget: 50000,
        max_budget: 200000,
        delivery_days: 14,
        creator_count: 5,
        verified_only: false,
        gender: 'any',
        creator_tier: 'any',
        has_contact_info: false,
        active_last_days: 30,
        avg_likes_min: 0,
        avg_comments_min: 0,
        monitoring_weeks: 2,
        content_brief: '',
        content_script: '',
        dos_and_donts: '',
        hashtags: '',
        reference_links: '',
        key_messages: '',
        cta_requirements: ''
      });
      setPrimaryCreators([]);
      setBackupCreators([]);
      setAiResults([]);
      setHasSearched(false);
      setCampaignId(null);
      setCurrentStep(1);
      setError('');
      
      // Clear URL params
      setSearchParams({}, { replace: true });
    }
  };

  // Options
  const languages = [
    'English', 'Hindi', 'Tamil', 'Telugu', 'Kannada', 'Malayalam', 
    'Marathi', 'Bengali', 'Gujarati', 'Punjabi'
  ];

  const creatorTiers = [
    { id: 'any', label: 'Any Tier' },
    { id: 'nano', label: 'Nano (1K-10K)' },
    { id: 'micro', label: 'Micro (10K-100K)' },
    { id: 'mid', label: 'Mid-tier (100K-500K)' },
    { id: 'macro', label: 'Macro (500K-1M)' },
    { id: 'mega', label: 'Mega (1M+)' }
  ];
  const niches = [
    'Fashion', 'Beauty', 'Lifestyle', 'Tech', 'Food', 'Travel', 'Fitness', 
    'Finance', 'Gaming', 'Entertainment', 'Education', 'Parenting', 'Health',
    'Home & Decor', 'Automotive', 'Sports', 'Music', 'Art', 'Photography'
  ];
  
  const cities = [
    'Mumbai', 'Delhi', 'Bangalore', 'Hyderabad', 'Chennai', 'Kolkata', 
    'Pune', 'Ahmedabad', 'Jaipur', 'Lucknow', 'Surat', 'Indore'
  ];
  
  const contentTypes = [
    { id: 'reel', label: 'Reels', icon: '🎬', desc: 'Short-form video, high reach & engagement' },
    { id: 'post', label: 'Posts', icon: '📸', desc: 'Photo or carousel, great for product showcase' }
  ];
  
  const objectives = [
    { id: 'Awareness', label: 'Brand Awareness', desc: 'Maximize reach and impressions' },
    { id: 'Engagement', label: 'Engagement', desc: 'Drive likes, comments, shares' },
    { id: 'Conversion', label: 'Conversion', desc: 'Drive sales and sign-ups' }
  ];

  // Handlers
  const handleInputChange = (field, value) => {
    setCampaignData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Validate budget range
      if (field === 'min_budget' && value > prev.max_budget) {
        newData.max_budget = value;
      }
      if (field === 'max_budget' && value < prev.min_budget) {
        newData.min_budget = value;
      }
      
      // Ensure positive values
      if (field === 'min_budget' || field === 'max_budget') {
        newData[field] = Math.max(0, value || 0);
      }
      
      return newData;
    });
  };

  const toggleArrayItem = (field, item) => {
    setCampaignData(prev => {
      const arr = prev[field];
      if (arr.includes(item)) {
        return { ...prev, [field]: arr.filter(i => i !== item) };
      } else {
        return { ...prev, [field]: [...arr, item] };
      }
    });
  };

  // Check if creator is already selected
  const isCreatorSelected = useCallback((creatorId) => {
    return primaryCreators.some(c => c.id === creatorId) || 
           backupCreators.some(c => c.id === creatorId);
  }, [primaryCreators, backupCreators]);

  // Add creator to primary list
  const addToPrimary = useCallback((creator) => {
    if (!isCreatorSelected(creator.id)) {
      setPrimaryCreators(prev => [...prev, creator]);
    }
  }, [isCreatorSelected]);

  // Add creator to backup list
  const addToBackup = useCallback((creator) => {
    if (!isCreatorSelected(creator.id)) {
      setBackupCreators(prev => [...prev, creator]);
    }
  }, [isCreatorSelected]);

  // Remove from primary
  const removeFromPrimary = useCallback((creatorId) => {
    setPrimaryCreators(prev => prev.filter(c => c.id !== creatorId));
  }, []);

  // Remove from backup
  const removeFromBackup = useCallback((creatorId) => {
    setBackupCreators(prev => prev.filter(c => c.id !== creatorId));
  }, []);

  // Move from primary to backup
  const moveToPrimary = useCallback((creatorId) => {
    const creator = backupCreators.find(c => c.id === creatorId);
    if (creator) {
      setBackupCreators(prev => prev.filter(c => c.id !== creatorId));
      setPrimaryCreators(prev => [...prev, creator]);
    }
  }, [backupCreators]);

  // Move from backup to primary
  const moveToBackup = useCallback((creatorId) => {
    const creator = primaryCreators.find(c => c.id === creatorId);
    if (creator) {
      setPrimaryCreators(prev => prev.filter(c => c.id !== creatorId));
      setBackupCreators(prev => [...prev, creator]);
    }
  }, [primaryCreators]);

  // Reorder backup (move up/down)
  const moveBackupUp = useCallback((index) => {
    if (index <= 0) return;
    setBackupCreators(prev => {
      const newList = [...prev];
      [newList[index - 1], newList[index]] = [newList[index], newList[index - 1]];
      return newList;
    });
  }, []);

  const moveBackupDown = useCallback((index) => {
    setBackupCreators(prev => {
      if (index >= prev.length - 1) return prev;
      const newList = [...prev];
      [newList[index], newList[index + 1]] = [newList[index + 1], newList[index]];
      return newList;
    });
  }, []);

  // Update creator price while typing (store raw value)
  const updateCreatorPrice = useCallback((creatorId, newPrice, isPrimary = true) => {
    const price = parseFloat(newPrice) || 0;
    if (isPrimary) {
      setPrimaryCreators(prev => prev.map(c =>
        c.id === creatorId
          ? { ...c, adjusted_price: price, pricing: { ...c.pricing, suggested_price: price } }
          : c
      ));
    } else {
      setBackupCreators(prev => prev.map(c =>
        c.id === creatorId
          ? { ...c, adjusted_price: price, pricing: { ...c.pricing, suggested_price: price } }
          : c
      ));
    }
  }, []);

  // Normalize price on blur — whole rupees, minimum ₹1 (no forced multiples of ₹100)
  const validateCreatorPrice = useCallback((creatorId, isPrimary = true) => {
    const updateFn = isPrimary ? setPrimaryCreators : setBackupCreators;
    updateFn(prev => prev.map(c => {
      if (c.id !== creatorId) return c;
      const rawPrice = c.adjusted_price ?? c.pricing?.suggested_price ?? MIN_CREATOR_PRICE_INR;
      const validPrice = normalizeCreatorPriceInr(rawPrice);
      return { ...c, adjusted_price: validPrice, pricing: { ...c.pricing, suggested_price: validPrice } };
    }));
  }, []);

  // Use AI Recommended (auto-select based on creator_count)
  const useAiRecommended = useCallback(() => {
    const availableCreators = aiResults.filter(c => !isCreatorSelected(c.id));
    const primaryCount = campaignData.creator_count;
    const backupCount = Math.ceil(primaryCount * 0.5); // 50% backups
    
    const newPrimary = availableCreators.slice(0, primaryCount);
    const newBackup = availableCreators.slice(primaryCount, primaryCount + backupCount);
    
    setPrimaryCreators(prev => [...prev, ...newPrimary]);
    setBackupCreators(prev => [...prev, ...newBackup]);
  }, [aiResults, isCreatorSelected, campaignData.creator_count]);

  // Fetch CRM creators (Saved Only)
  const fetchCrmCreators = useCallback(async () => {
    setLoadingCrm(true);
    try {
      const savedRes = await getSavedCreators(brandId).catch(() => ({ data: { creators: [] } }));
      setCrmCreators(savedRes.data?.creators || []);
    } catch (err) {
      console.error('Error fetching CRM creators:', err);
    } finally {
      setLoadingCrm(false);
    }
  }, [brandId]);

  // Load CRM creators on mount (to show correct count) and when panel is opened
  useEffect(() => {
    if (brandId) {
      fetchCrmCreators();
    }
  }, [brandId, fetchCrmCreators]);

  // Simulate campaign
  const handleSimulate = async () => {
    if (!campaignData.campaign_name) {
      setError('Please enter a campaign name');
      return;
    }
    if (campaignData.niches.length === 0) {
      setError('Please select at least one niche');
      return;
    }
    if (campaignData.content_types.length === 0) {
      setError('Please select at least one content type');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Convert string fields to arrays where needed for API
      const hashtagsArray = campaignData.hashtags
        ? campaignData.hashtags.split(/[\s,]+/).filter(h => h.trim())
        : [];
      
      const referenceLinksArray = campaignData.reference_links
        ? campaignData.reference_links.split('\n').filter(l => l.trim())
        : [];
      
      const keyMessagesArray = campaignData.key_messages
        ? campaignData.key_messages.split('\n').filter(m => m.trim())
        : [];

      const response = await simulateCampaign({
        ...campaignData,
        hashtags: hashtagsArray,
        reference_links: referenceLinksArray,
        key_messages: keyMessagesArray,
        brand_id: brandId
      });

      if (response.data.success) {
        const allCreators = [
          ...(response.data.primary_creators || []),
          ...(response.data.backup_creators || [])
        ];
        setAiResults(allCreators);
        setHasSearched(true);
        setCurrentStep(2);
      } else {
        setError(response.data.message || 'No creators found');
      }
    } catch (err) {
      // Handle FastAPI validation errors which return array of objects
      const errorDetail = err.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        // Extract messages from validation error array
        const messages = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        setError(messages || 'Validation error');
      } else if (typeof errorDetail === 'object' && errorDetail !== null) {
        setError(errorDetail.msg || errorDetail.message || JSON.stringify(errorDetail));
      } else {
        setError(errorDetail || 'Failed to simulate campaign');
      }
    } finally {
      setLoading(false);
    }
  };

  // Show price edit modal before creating campaign
  const handleContinueToReview = () => {
    if (primaryCreators.length === 0) {
      setError('Please select at least one primary creator');
      return;
    }

    if (!campaignData.content_brief.trim()) {
      setError('Please provide a content brief for creators');
      return;
    }

    setError('');
    setShowPriceEditModal(true);
  };

  // Create and finalize campaign (called after price editing)
  const handleCreateCampaign = async () => {
    const validateAndRoundPrices = (creators) =>
      creators.map((c) => {
        const rawPrice = c.adjusted_price ?? c.pricing?.suggested_price ?? MIN_CREATOR_PRICE_INR;
        const validPrice = normalizeCreatorPriceInr(rawPrice);
        return { ...c, adjusted_price: validPrice, pricing: { ...c.pricing, suggested_price: validPrice } };
      });

    const validatedPrimary = validateAndRoundPrices(primaryCreators);
    const validatedBackup = validateAndRoundPrices(backupCreators);
    
    setPrimaryCreators(validatedPrimary);
    setBackupCreators(validatedBackup);

    setShowPriceEditModal(false);
    setLoading(true);
    setError('');

    try {
      // Convert string fields to arrays where needed
      const hashtagsArray = campaignData.hashtags
        ? campaignData.hashtags.split(/[\s,]+/).filter(h => h.trim())
        : [];
      
      const referenceLinksArray = campaignData.reference_links
        ? campaignData.reference_links.split('\n').filter(l => l.trim())
        : [];
      
      const keyMessagesArray = campaignData.key_messages
        ? campaignData.key_messages.split('\n').filter(m => m.trim())
        : [];

      const response = await createCampaignFromSimulation({
        ...campaignData,
        hashtags: hashtagsArray,
        reference_links: referenceLinksArray,
        key_messages: keyMessagesArray,
        brand_id: brandId,
        selected_creators: validatedPrimary,
        backup_creators: validatedBackup,
        projections: calculateProjections()
      });

      if (response.data.success) {
        setCampaignId(response.data.campaign_id);
        setCurrentStep(3);
      }
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        const messages = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        setError(messages || 'Validation error');
      } else if (typeof errorDetail === 'object' && errorDetail !== null) {
        setError(errorDetail.msg || errorDetail.message || JSON.stringify(errorDetail));
      } else {
        setError(errorDetail || 'Failed to create campaign');
      }
    } finally {
      setLoading(false);
    }
  };

  // Show payment modal - after payment success, invitations are sent automatically
  const handleShowPayment = () => {
    setShowPaymentModal(true);
  };

  // User clicks "Send invitations now" on the recovery banner. Server already
  // has the locked payment + saved allocation, so we just send the
  // campaign_id and the finalize endpoint fills in the rest.
  const handleResumeFinalize = async () => {
    if (!campaignId) return;
    setRecoverableRazorpay(prev => ({ ...(prev || {}), finalizing: true, error: null }));
    setError('');
    try {
      const response = await finalizeCampaign({ campaign_id: campaignId });
      if (response?.data?.success) {
        setRecoverableRazorpay(null);
        setCurrentStep(4);
      } else {
        throw new Error(response?.data?.message || 'Finalize failed');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to finalize campaign';
      setRecoverableRazorpay(prev => ({ ...(prev || {}), finalizing: false, error: msg }));
      setError(msg);
    }
  };

  // Handle payment success - directly send invitations (no confirmation modal needed)
  const handlePaymentSuccess = async (paymentResult) => {
    setShowPaymentModal(false);
    setLoading(true);
    setError('');

    try {
      // Calculate creator prices to send to backend
      const creatorPrices = {};
      primaryCreators.forEach(c => {
        creatorPrices[c.id] = c.adjusted_price || c.pricing?.suggested_price || 0;
      });
      backupCreators.forEach(c => {
        creatorPrices[c.id] = c.adjusted_price || c.pricing?.suggested_price || 0;
      });

      const response = await finalizeCampaign({
        campaign_id: campaignId,
        selected_creator_ids: primaryCreators.map(c => c.id),
        backup_creator_ids: backupCreators.map(c => c.id),
        payment_id: paymentResult.razorpay_payment_id,
        order_id: paymentResult.razorpay_order_id,
        creator_prices: creatorPrices
      });

      if (response.data.success) {
        setCurrentStep(4);
      }
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        const messages = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        setError(messages || 'Validation error');
      } else if (typeof errorDetail === 'object' && errorDetail !== null) {
        setError(errorDetail.msg || errorDetail.message || JSON.stringify(errorDetail));
      } else {
        setError(errorDetail || 'Failed to finalize campaign');
      }
    } finally {
      setLoading(false);
    }
  };

  // Delete campaign (only available before sending emails)
  const handleDeleteCampaign = async () => {
    setLoading(true);
    setError('');
    setShowDeleteModal(false);

    try {
      const response = await deleteCampaign(campaignId);

      if (response.data.success) {
        // Reset everything and go back to step 1
        setCampaignId(null);
        setPrimaryCreators([]);
        setBackupCreators([]);
        setAiResults([]);
        setHasSearched(false);
        setCurrentStep(1);
        setError('');
        alert('Campaign deleted successfully');
      }
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        const messages = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        setError(messages || 'Validation error');
      } else if (typeof errorDetail === 'object' && errorDetail !== null) {
        setError(errorDetail.msg || errorDetail.message || JSON.stringify(errorDetail));
      } else {
        setError(errorDetail || 'Failed to delete campaign. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { 
      style: 'currency', 
      currency: 'INR', 
      maximumFractionDigits: 0 
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num || 0;
  };

  // Calculate projections from selected creators
  const calculateProjections = () => {
    if (!primaryCreators.length) return null;
    
    const totalFollowers = primaryCreators.reduce((sum, c) => sum + (c.instagram_followers_count || 0), 0);
    const avgEngagement = primaryCreators.reduce((sum, c) => sum + (c.instagram_engagement_rate || 2), 0) / primaryCreators.length;
    const totalCost = primaryCreators.reduce((sum, c) => sum + (c.pricing?.suggested_price || 0), 0);
    
    const estimatedReach = totalFollowers * 0.18;
    const estimatedEngagement = estimatedReach * (avgEngagement / 100);
    
    return {
      total_creators: primaryCreators.length,
      total_followers: totalFollowers,
      avg_engagement_rate: avgEngagement.toFixed(2),
      estimated_reach: Math.round(estimatedReach),
      estimated_engagement: Math.round(estimatedEngagement),
      total_cost: Math.round(totalCost),
      budget_remaining: campaignData.max_budget - totalCost
    };
  };

  const projections = calculateProjections();
  const totalSelectedCost = primaryCreators.reduce((sum, c) => sum + (c.pricing?.suggested_price || 0), 0);
  const budgetUsed = (totalSelectedCost / campaignData.max_budget) * 100;

  // Show loading state when loading a draft campaign
  if (loadingDraft) {
    return (
      <div className="create-campaign-page">
        <Navbar />
        <div className="campaign-container">
          <div className="loading-draft">
            <div className="loading-spinner"></div>
            <h2>Loading Campaign...</h2>
            <p>Please wait while we load your draft campaign</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="create-campaign-page">
      <Navbar />
      
      <div className="campaign-container">
        {/* Progress Steps */}
        <div className="campaign-progress">
          <div className={`cp-step ${currentStep === 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
            <div className="cp-circle">
              <span className="cp-num">1</span>
            </div>
            <span className="cp-label">Campaign Details</span>
          </div>
          <div className={`cp-line ${currentStep > 1 ? 'completed' : ''}`}></div>
          <div className={`cp-step ${currentStep === 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
            <div className="cp-circle">
              <span className="cp-num">2</span>
            </div>
            <span className="cp-label">Select Creators</span>
          </div>
          <div className={`cp-line ${currentStep > 2 ? 'completed' : ''}`}></div>
          <div className={`cp-step ${currentStep === 3 ? 'active' : ''} ${currentStep > 3 ? 'completed' : ''}`}>
            <div className="cp-circle">
              <span className="cp-num">3</span>
            </div>
            <span className="cp-label">Review & Finalize</span>
          </div>
          <div className={`cp-line ${currentStep > 3 ? 'completed' : ''}`}></div>
          <div className={`cp-step ${currentStep === 4 ? 'active' : ''}`}>
            <div className="cp-circle">
              <span className="cp-num">4</span>
            </div>
            <span className="cp-label">Launch</span>
          </div>
        </div>

        {/* Draft Resume Banner & Start Fresh */}
        {currentStep > 1 && currentStep < 4 && !urlCampaignId && (
          <div className="draft-actions-bar">
            <div className="draft-info">
              <span className="draft-indicator">📝</span>
              <span>Your progress is automatically saved</span>
            </div>
            <button className="btn-start-fresh" onClick={handleStartFresh}>
              🔄 Start Fresh
            </button>
          </div>
        )}

        <div ref={topRef}></div>
        {error && <div ref={errorRef} className="campaign-error">{error}</div>}

        {/* Step 1: Campaign Details */}
        {currentStep === 1 && (
          <div className="campaign-step step-1">
            <div className="step-header-with-actions">
              <div>
                <h1>Create New Campaign</h1>
                <p className="step-subtitle">Define your campaign parameters and let AI find the perfect creators</p>
              </div>
              {!urlCampaignId && hasRestoredFromStorage && (hasSearched || primaryCreators.length > 0 || campaignData.campaign_name) && (
                <button className="btn-start-fresh" onClick={handleStartFresh}>
                  🔄 Start Fresh
                </button>
              )}
            </div>

            {/* Platform Info Banner */}
            <div className="platform-info-banner">
              <div className="platform-info-content">
                <div className="platform-active">
                  <span className="platform-icon-mini">📸</span>
                  <span>Instagram Creators</span>
                  <span className="platform-status-badge active">Active</span>
                </div>
                <div className="platform-divider"></div>
                <div className="platform-coming">
                  <span className="coming-soon-label">Coming Soon:</span>
                  <span className="platform-icon-mini youtube">▶️</span>
                  <span>YouTube</span>
                  <span className="coming-dot">•</span>
                  <span className="platform-icon-mini twitter">𝕏</span>
                  <span>Twitter</span>
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Basic Information</h3>
              <div className="form-group">
                <label>Campaign Name *</label>
                <input
                  type="text"
                  value={campaignData.campaign_name}
                  onChange={(e) => handleInputChange('campaign_name', e.target.value)}
                  placeholder="e.g., Summer Fashion Collection Launch"
                />
              </div>

              <div className="form-group">
                <label>Campaign Objective</label>
                <div className="objective-cards">
                  {objectives.map(obj => (
                    <div
                      key={obj.id}
                      className={`objective-card ${campaignData.objective === obj.id ? 'selected' : ''}`}
                      onClick={() => handleInputChange('objective', obj.id)}
                    >
                      <h4>{obj.label}</h4>
                      <p>{obj.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Content Requirements</h3>
              <div className="form-group">
                <label>Content Types *</label>
                <p className="field-hint">Select the type of content you want creators to produce</p>
                <div className="content-type-grid">
                  {contentTypes.map(ct => (
                    <div
                      key={ct.id}
                      className={`content-type-card ${campaignData.content_types.includes(ct.id) ? 'selected' : ''}`}
                      onClick={() => toggleArrayItem('content_types', ct.id)}
                    >
                      <span className="ct-icon">{ct.icon}</span>
                      <div className="ct-text">
                        <span className="ct-label">{ct.label}</span>
                        <span className="ct-desc">{ct.desc}</span>
                      </div>
                      {campaignData.content_types.includes(ct.id) && (
                        <span className="ct-check">✓</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Target Niches * (Select up to 5)</label>
                <div className="niche-chips">
                  {niches.map(niche => (
                    <span
                      key={niche}
                      className={`niche-chip ${campaignData.niches.includes(niche) ? 'selected' : ''}`}
                      onClick={() => {
                        if (campaignData.niches.length < 5 || campaignData.niches.includes(niche)) {
                          toggleArrayItem('niches', niche);
                        }
                      }}
                    >
                      {niche}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Creator Criteria</h3>
              
              {/* Follower Range */}
              <div className="form-row">
                <div className="form-group">
                  <label>Min Followers</label>
                  <select
                    value={campaignData.min_followers}
                    onChange={(e) => handleInputChange('min_followers', parseInt(e.target.value))}
                  >
                    <option value={1000}>1K+</option>
                    <option value={5000}>5K+</option>
                    <option value={10000}>10K+</option>
                    <option value={25000}>25K+</option>
                    <option value={50000}>50K+</option>
                    <option value={100000}>100K+</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Max Followers</label>
                  <select
                    value={campaignData.max_followers}
                    onChange={(e) => handleInputChange('max_followers', parseInt(e.target.value))}
                  >
                    <option value={50000}>50K</option>
                    <option value={100000}>100K</option>
                    <option value={250000}>250K</option>
                    <option value={500000}>500K</option>
                    <option value={1000000}>1M</option>
                    <option value={5000000}>5M+</option>
                  </select>
                </div>
              </div>

              {/* Engagement Rate Range */}
              <div className="form-row">
                <div className="form-group">
                  <label>Min Engagement Rate (%)</label>
                  <select
                    value={campaignData.min_engagement_rate}
                    onChange={(e) => handleInputChange('min_engagement_rate', parseFloat(e.target.value))}
                  >
                    <option value={0}>Any</option>
                    <option value={1}>1%+</option>
                    <option value={2}>2%+</option>
                    <option value={3}>3%+</option>
                    <option value={5}>5%+</option>
                    <option value={7}>7%+</option>
                    <option value={10}>10%+</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Max Engagement Rate (%)</label>
                  <select
                    value={campaignData.max_engagement_rate}
                    onChange={(e) => handleInputChange('max_engagement_rate', parseFloat(e.target.value))}
                  >
                    <option value={5}>Up to 5%</option>
                    <option value={10}>Up to 10%</option>
                    <option value={15}>Up to 15%</option>
                    <option value={20}>Up to 20%</option>
                    <option value={100}>Any</option>
                  </select>
                </div>
              </div>

              {/* Creator Tier & Gender */}
              <div className="form-row">
                <div className="form-group">
                  <label>Creator Tier</label>
                  <select
                    value={campaignData.creator_tier}
                    onChange={(e) => handleInputChange('creator_tier', e.target.value)}
                  >
                    {creatorTiers.map(tier => (
                      <option key={tier.id} value={tier.id}>{tier.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Gender Preference</label>
                  <select
                    value={campaignData.gender}
                    onChange={(e) => handleInputChange('gender', e.target.value)}
                  >
                    <option value="any">Any</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>
              </div>

              {/* Languages */}
              <div className="form-group">
                <label>Languages</label>
                <div className="language-chips">
                  {languages.map(lang => (
                    <span
                      key={lang}
                      className={`language-chip ${campaignData.languages.includes(lang) ? 'selected' : ''}`}
                      onClick={() => toggleArrayItem('languages', lang)}
                    >
                      {lang}
                    </span>
                  ))}
                </div>
              </div>

              {/* Target Cities */}
              <div className="form-group">
                <label>Target Cities (Optional)</label>
                <div className="city-chips">
                  {cities.map(city => (
                    <span
                      key={city}
                      className={`city-chip ${campaignData.target_cities.includes(city) ? 'selected' : ''}`}
                      onClick={() => toggleArrayItem('target_cities', city)}
                    >
                      {city}
                    </span>
                  ))}
                </div>
              </div>

              {/* Additional Filters */}
              <div className="form-row">
                <div className="form-group">
                  <label>Number of Creators</label>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={campaignData.creator_count}
                    onChange={(e) => handleInputChange('creator_count', parseInt(e.target.value))}
                  />
                </div>
                <div className="form-group">
                  <label>Active in Last (Days)</label>
                  <select
                    value={campaignData.active_last_days}
                    onChange={(e) => handleInputChange('active_last_days', parseInt(e.target.value))}
                  >
                    <option value={7}>7 days</option>
                    <option value={14}>14 days</option>
                    <option value={30}>30 days</option>
                    <option value={60}>60 days</option>
                    <option value={90}>90 days</option>
                  </select>
                </div>
              </div>

              {/* Checkboxes */}
              <div className="form-checkboxes">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={campaignData.verified_only}
                    onChange={(e) => handleInputChange('verified_only', e.target.checked)}
                  />
                  <span>Verified Creators Only</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={campaignData.has_contact_info}
                    onChange={(e) => handleInputChange('has_contact_info', e.target.checked)}
                  />
                  <span>Has Contact Info</span>
                </label>
              </div>
            </div>

            <div className="form-section">
              <h3>Budget & Timeline</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Minimum Budget (₹)</label>
                  <input
                    type="number"
                    value={campaignData.min_budget}
                    onChange={(e) => handleInputChange('min_budget', parseInt(e.target.value))}
                    placeholder="50000"
                  />
                </div>
                <div className="form-group">
                  <label>Maximum Budget (₹)</label>
                  <input
                    type="number"
                    value={campaignData.max_budget}
                    onChange={(e) => handleInputChange('max_budget', parseInt(e.target.value))}
                    placeholder="200000"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>
                  ⏰ Content Submission Deadline
                  <span className="label-hint">Time for creators to create and submit content for review. If a creator accepts but doesn't submit by this deadline, the invitation auto-transfers to the next backup creator.</span>
                </label>
                <select
                  value={campaignData.delivery_days}
                  onChange={(e) => handleInputChange('delivery_days', parseInt(e.target.value))}
                >
                  <option value={3}>3 Days</option>
                  <option value={5}>5 Days</option>
                  <option value={7}>1 Week</option>
                  <option value={14}>2 Weeks (Recommended)</option>
                  <option value={21}>3 Weeks</option>
                  <option value={28}>4 Weeks (Max)</option>
                </select>
              </div>

              {/* Monitoring Duration Selection */}
              <div className="form-group monitoring-duration-group">
                <label>
                  📊 Performance Monitoring Period
                  <span className="label-hint">Track engagement metrics after content goes live on Instagram</span>
                </label>
                <div className="monitoring-options">
                  {MONITORING_DURATION_OPTIONS.map(option => {
                    const isSelected = campaignData.monitoring_weeks === option.value;
                    return (
                      <div
                        key={option.value}
                        className={`monitoring-option ${isSelected ? 'selected' : ''}`}
                        onClick={() => setCampaignData(prev => ({ ...prev, monitoring_weeks: option.value }))}
                      >
                        <div className="option-header">
                          <span className="option-value">{option.value}</span>
                          <span className="option-unit">{`Week${option.value > 1 ? 's' : ''}`}</span>
                          {option.recommended && (
                            <span className="recommended-badge">Recommended</span>
                          )}
                        </div>
                        <p className="option-description">{option.description}</p>
                      </div>
                    );
                  })}
                </div>
                <div className="monitoring-info">
                  <span className="info-icon">ℹ️</span>
                  <p>
                    During monitoring, we track engagement metrics (likes, comments, shares).
                    <strong> {Math.round(FIXED_PAYMENT_PERCENTAGE * 100)}% fixed payment</strong> is released weekly,
                    and <strong>{Math.round(PERFORMANCE_PAYMENT_PERCENTAGE * 100)}% performance bonus</strong> is calculated based on total metrics at the end.
                    A <strong>3.5% platform fee</strong> is added on top of your campaign budget at checkout (creators receive their full quoted amount).
                  </p>
                </div>
              </div>
            </div>

            {/* Content Brief Section */}
            <div className="form-section content-brief-section">
              <h3>📝 Content Brief for Creators</h3>
              <p className="section-description">
                Provide clear guidelines for creators to follow. This information will be shared with selected creators.
              </p>
              
              <div className="form-group">
                <label>Campaign Brief *</label>
                <textarea
                  value={campaignData.content_brief}
                  onChange={(e) => handleInputChange('content_brief', e.target.value)}
                  placeholder="Describe your campaign goals, target audience, and what you want creators to achieve..."
                  rows={4}
                />
              </div>

              <div className="form-group">
                <label>Content Script / Talking Points</label>
                <textarea
                  value={campaignData.content_script}
                  onChange={(e) => handleInputChange('content_script', e.target.value)}
                  placeholder="Provide specific script, talking points, or content structure for creators to follow..."
                  rows={4}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Do's and Don'ts</label>
                  <textarea
                    value={campaignData.dos_and_donts}
                    onChange={(e) => handleInputChange('dos_and_donts', e.target.value)}
                    placeholder="List important guidelines:&#10;DO: Mention product benefits&#10;DO: Use natural lighting&#10;DON'T: Use competitor names&#10;DON'T: Make medical claims"
                    rows={4}
                  />
                </div>
                <div className="form-group">
                  <label>Key Messages</label>
                  <textarea
                    value={campaignData.key_messages}
                    onChange={(e) => handleInputChange('key_messages', e.target.value)}
                    placeholder="Enter key messages (one per line):&#10;Premium quality&#10;100% natural&#10;Made in India"
                    rows={4}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Hashtags</label>
                  <input
                    type="text"
                    value={campaignData.hashtags}
                    onChange={(e) => handleInputChange('hashtags', e.target.value)}
                    placeholder="#YourBrand #CampaignName #Hashtag"
                  />
                </div>
                <div className="form-group">
                  <label>CTA Requirements</label>
                  <input
                    type="text"
                    value={campaignData.cta_requirements}
                    onChange={(e) => handleInputChange('cta_requirements', e.target.value)}
                    placeholder="e.g., Link in bio, Swipe up, Use code XYZ"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Reference Links</label>
                <textarea
                  value={campaignData.reference_links}
                  onChange={(e) => handleInputChange('reference_links', e.target.value)}
                  placeholder="Add reference URLs (one per line):&#10;https://example.com/reference1&#10;https://instagram.com/p/example"
                  rows={2}
                />
              </div>
            </div>

            <div className="step-actions">
              <button className="btn-secondary" onClick={() => navigate('/brand-dashboard')}>
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleSimulate}
                disabled={loading}
              >
                {loading ? 'Finding Creators...' : 'Get AI Recommendations →'}
              </button>
            </div>
          </div>
        )}

        {/* Step 2: AI Workspace - Select Creators */}
        {currentStep === 2 && (
          <div className="campaign-step step-2 workspace-layout">
            <div className="workspace-container">
              {/* Left Panel - Parameters & Results */}
              <div className="workspace-left">
                {/* Collapsible Parameters */}
                <div className={`params-panel ${showParamsPanel ? 'expanded' : 'collapsed'}`}>
                  <div className="params-header" onClick={() => setShowParamsPanel(!showParamsPanel)}>
                    <h3>🔧 Search Parameters</h3>
                    <span className="toggle-icon">{showParamsPanel ? '−' : '+'}</span>
                  </div>
                  
                  {showParamsPanel && (
                    <div className="params-content">
                      <div className="params-grid">
                        {/* Niches */}
                        <div className="param-group">
                          <label>Niches</label>
                          <div className="mini-chips">
                            {niches.map(niche => (
                              <span
                                key={niche}
                                className={`mini-chip ${campaignData.niches.includes(niche) ? 'selected' : ''}`}
                                onClick={() => toggleArrayItem('niches', niche)}
                              >
                                {niche}
                              </span>
                            ))}
                          </div>
                        </div>
                        
                        {/* Follower Range */}
                        <div className="param-row">
                          <div className="param-group small">
                            <label>Min Followers</label>
                            <select
                              value={campaignData.min_followers}
                              onChange={(e) => handleInputChange('min_followers', parseInt(e.target.value))}
                            >
                              <option value={1000}>1K</option>
                              <option value={5000}>5K</option>
                              <option value={10000}>10K</option>
                              <option value={50000}>50K</option>
                              <option value={100000}>100K</option>
                            </select>
                          </div>
                          <div className="param-group small">
                            <label>Max Followers</label>
                            <select
                              value={campaignData.max_followers}
                              onChange={(e) => handleInputChange('max_followers', parseInt(e.target.value))}
                            >
                              <option value={100000}>100K</option>
                              <option value={250000}>250K</option>
                              <option value={500000}>500K</option>
                              <option value={1000000}>1M</option>
                              <option value={5000000}>5M+</option>
                            </select>
                          </div>
                          <div className="param-group small">
                            <label>Count</label>
                            <input
                              type="number"
                              min="1"
                              max="50"
                              value={campaignData.creator_count}
                              onChange={(e) => handleInputChange('creator_count', parseInt(e.target.value))}
                            />
                          </div>
                        </div>

                        {/* Engagement Rate Range */}
                        <div className="param-row">
                          <div className="param-group small">
                            <label>Min Eng. Rate</label>
                            <select
                              value={campaignData.min_engagement_rate}
                              onChange={(e) => handleInputChange('min_engagement_rate', parseFloat(e.target.value))}
                            >
                              <option value={0}>Any</option>
                              <option value={1}>1%+</option>
                              <option value={2}>2%+</option>
                              <option value={3}>3%+</option>
                              <option value={5}>5%+</option>
                            </select>
                          </div>
                          <div className="param-group small">
                            <label>Max Eng. Rate</label>
                            <select
                              value={campaignData.max_engagement_rate}
                              onChange={(e) => handleInputChange('max_engagement_rate', parseFloat(e.target.value))}
                            >
                              <option value={10}>≤10%</option>
                              <option value={15}>≤15%</option>
                              <option value={100}>Any</option>
                            </select>
                          </div>
                          <div className="param-group small">
                            <label>Gender</label>
                            <select
                              value={campaignData.gender}
                              onChange={(e) => handleInputChange('gender', e.target.value)}
                            >
                              <option value="any">Any</option>
                              <option value="male">Male</option>
                              <option value="female">Female</option>
                            </select>
                          </div>
                        </div>

                        {/* Cities */}
                        <div className="param-group">
                          <label>Target Cities</label>
                          <div className="mini-chips">
                            {cities.map(city => (
                              <span
                                key={city}
                                className={`mini-chip ${campaignData.target_cities.includes(city) ? 'selected' : ''}`}
                                onClick={() => toggleArrayItem('target_cities', city)}
                              >
                                {city}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Quick Filters */}
                        <div className="param-filters">
                          <label className="filter-checkbox">
                            <input
                              type="checkbox"
                              checked={campaignData.verified_only}
                              onChange={(e) => handleInputChange('verified_only', e.target.checked)}
                            />
                            <span>Verified Only</span>
                          </label>
                          <label className="filter-checkbox">
                            <input
                              type="checkbox"
                              checked={campaignData.has_contact_info}
                              onChange={(e) => handleInputChange('has_contact_info', e.target.checked)}
                            />
                            <span>Has Contact</span>
                          </label>
                        </div>
                      </div>
                      
                      <button 
                        className="btn-search" 
                        onClick={handleSimulate}
                        disabled={loading}
                      >
                        {loading ? '🔄 Searching...' : '🔍 Search Again'}
                      </button>
                    </div>
                  )}
                </div>

                {/* Source Toggle - AI vs CRM */}
                <div className="source-toggle">
                  <button 
                    className={`source-btn ${!showCrmPanel ? 'active' : ''}`}
                    onClick={() => setShowCrmPanel(false)}
                  >
                    🤖 AI Recommendations ({aiResults.length})
                  </button>
                  <button 
                    className={`source-btn ${showCrmPanel ? 'active' : ''}`}
                    onClick={() => setShowCrmPanel(true)}
                  >
                    📚 Saved Creators ({crmCreators.length})
                  </button>
                </div>

                {/* AI Results Panel */}
                {!showCrmPanel && (
                <div className="results-panel">
                  <div className="results-header">
                    <h3>🤖 AI Recommendations ({aiResults.length})</h3>
                    {aiResults.length > 0 && (
                      <button 
                        className="btn-auto-select"
                        onClick={useAiRecommended}
                        disabled={aiResults.filter(c => !isCreatorSelected(c.id)).length === 0}
                      >
                        ✨ Use AI Recommended
                      </button>
                    )}
                  </div>
                  
                  {!hasSearched ? (
                    <div className="results-empty">
                      <p>Click "Get AI Recommendations" to find creators</p>
                    </div>
                  ) : aiResults.length === 0 ? (
                    <div className="results-empty">
                      <p>No creators found. Try adjusting your parameters.</p>
                    </div>
                  ) : (
                    <div className="results-grid">
                      {aiResults.map(creator => {
                        const inPrimary = primaryCreators.some(c => c.id === creator.id);
                        const inBackup = backupCreators.some(c => c.id === creator.id);
                        const selected = inPrimary || inBackup;
                        
                        return (
                          <div 
                            key={creator.id} 
                            className={`result-card ${inPrimary ? 'in-primary' : ''} ${inBackup ? 'in-backup' : ''}`}
                          >
                            <div className="result-header">
                              <img 
                                src={creator.instagram_profile_picture || `https://ui-avatars.com/api/?name=${creator.name}&background=6366f1&color=fff`}
                                alt={creator.name}
                                className="result-avatar"
                              />
                              <div className="result-info">
                                <h4>
                                  {creator.name}
                                  {creator.verified && <span className="verified-badge" title="Verified">✓</span>}
                                </h4>
                                <p>@{creator.instagram_username}</p>
                              </div>
                              <span className="match-badge">{creator.relevance_score}%</span>
                            </div>
                            
                            {/* Main Stats Grid */}
                            <div className="result-stats-grid">
                              <div className="stat-item">
                                <span className="stat-value">{formatNumber(creator.instagram_followers_count)}</span>
                                <span className="stat-label">Followers</span>
                              </div>
                              <div className="stat-item highlight">
                                <span className="stat-value">{creator.instagram_engagement_rate?.toFixed(2)}%</span>
                                <span className="stat-label">Engagement</span>
                              </div>
                              <div className="stat-item">
                                <span className="stat-value">{formatNumber(creator.instagram_media_count || 0)}</span>
                                <span className="stat-label">Posts</span>
                              </div>
                            </div>

                            {/* Secondary Stats */}
                            <div className="result-stats-secondary">
                              {creator.instagram_avg_likes_per_post && (
                                <span className="stat-mini">
                                  ❤️ {formatNumber(creator.instagram_avg_likes_per_post)} avg likes
                                </span>
                              )}
                              {creator.instagram_avg_comments_per_post && (
                                <span className="stat-mini">
                                  💬 {formatNumber(creator.instagram_avg_comments_per_post)} avg comments
                                </span>
                              )}
                            </div>
                            
                            <div className="result-niches">
                              {(creator.instagram_detected_niches || creator.creator_preferences_content_categories || []).slice(0, 3).map((n, i) => (
                                <span key={i} className="niche-mini">{n}</span>
                              ))}
                            </div>

                            {/* Location & Language */}
                            <div className="result-meta">
                              {creator.city && <span className="meta-item">📍 {creator.city}</span>}
                              {(creator.languages || creator.creator_preferences_preferred_language) && (
                                <span className="meta-item">
                                  🗣️ {(creator.languages || [creator.creator_preferences_preferred_language]).slice(0, 2).join(', ')}
                                </span>
                              )}
                            </div>
                            
                            <div className="result-price">
                              <span className="price-label">Est. Price</span>
                              <span className="price-value">{formatCurrency(creator.pricing?.suggested_price)}</span>
                            </div>
                            
                            {selected ? (
                              <div className="result-selected-badge">
                                {inPrimary ? '✓ Primary' : '✓ Backup'}
                              </div>
                            ) : (
                              <div className="result-actions">
                                <button 
                                  className="btn-add-primary"
                                  onClick={() => addToPrimary(creator)}
                                >
                                  + Primary
                                </button>
                                <button 
                                  className="btn-add-backup"
                                  onClick={() => addToBackup(creator)}
                                >
                                  + Backup
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                )}

                {/* CRM Panel - Saved Creators Only */}
                {showCrmPanel && (
                <div className="results-panel crm-panel">
                  <div className="results-header">
                    <h3>📚 Saved Creators ({crmCreators.length})</h3>
                    <button 
                      className="btn-refresh"
                      onClick={fetchCrmCreators}
                      disabled={loadingCrm}
                    >
                      {loadingCrm ? '⏳' : '🔄'}
                    </button>
                  </div>

                  <div className="crm-search">
                    <input 
                      type="text"
                      placeholder="Search saved creators..."
                      value={crmSearchTerm}
                      onChange={(e) => setCrmSearchTerm(e.target.value)}
                    />
                  </div>
                  
                  {loadingCrm ? (
                    <div className="results-empty">
                      <p>Loading saved creators...</p>
                    </div>
                  ) : crmCreators.filter(c => 
                      (c.name || c.instagram_username || '').toLowerCase().includes(crmSearchTerm.toLowerCase())
                    ).length === 0 ? (
                    <div className="results-empty">
                      <p>No saved creators yet. Save creators from Find Creators page or Brand Dashboard CRM.</p>
                    </div>
                  ) : (
                    <div className="results-grid">
                      {crmCreators.filter(c => 
                        (c.name || c.instagram_username || '').toLowerCase().includes(crmSearchTerm.toLowerCase())
                      ).map(creator => {
                        const inPrimary = primaryCreators.some(c => c.id === creator.id);
                        const inBackup = backupCreators.some(c => c.id === creator.id);
                        
                        return (
                          <div 
                            key={creator.id} 
                            className={`result-card crm-card ${inPrimary ? 'in-primary' : ''} ${inBackup ? 'in-backup' : ''}`}
                          >
                            <div className="result-header">
                              <img 
                                src={creator.instagram_profile_picture || `https://ui-avatars.com/api/?name=${creator.name || creator.instagram_name}&background=6366f1&color=fff`}
                                alt={creator.name || creator.instagram_name}
                                className="result-avatar"
                              />
                              <div className="result-info">
                                <h4>
                                  {creator.name || creator.instagram_name}
                                  {creator.verified && <span className="verified-badge" title="Verified">✓</span>}
                                </h4>
                                <p>@{creator.instagram_username}</p>
                              </div>
                            </div>
                            
                            <div className="result-stats-grid">
                              <div className="stat-item">
                                <span className="stat-value">{formatNumber(creator.instagram_followers_count)}</span>
                                <span className="stat-label">Followers</span>
                              </div>
                              <div className="stat-item highlight">
                                <span className="stat-value">
                                  {(creator.instagram_engagement_rate || 0).toFixed(2)}%
                                </span>
                                <span className="stat-label">Engagement</span>
                              </div>
                              <div className="stat-item">
                                <span className="stat-value">{formatNumber(creator.instagram_media_count || 0)}</span>
                                <span className="stat-label">Posts</span>
                              </div>
                            </div>
                            
                            {creator.instagram_detected_niches && (
                              <div className="result-niches">
                                {creator.instagram_detected_niches.slice(0, 3).map((n, i) => (
                                  <span key={i} className="niche-mini">{n}</span>
                                ))}
                              </div>
                            )}

                            {/* CRM Tags */}
                            {(creator.tags || creator.crm_data?.tags) && (
                              <div className="crm-tags">
                                {(creator.tags || creator.crm_data?.tags || []).map((tag, i) => (
                                  <span key={i} className="crm-tag">{tag}</span>
                                ))}
                              </div>
                            )}
                            
                            <div className="result-price">
                              <span className="price-label">Est. Price</span>
                              <span className="price-value">
                                {formatCurrency(
                                  creator.pricing?.suggested_price || 
                                  creator.creator_preferences_rate_per_reel ||
                                  (creator.total_earned && creator.total_campaigns ? creator.total_earned / creator.total_campaigns : 0)
                                )}
                              </span>
                            </div>
                            
                            {(inPrimary || inBackup) ? (
                              <div className="result-selected-badge">
                                {inPrimary ? '✓ Primary' : '✓ Backup'}
                              </div>
                            ) : (
                              <div className="result-actions">
                                <button 
                                  className="btn-add-primary"
                                  onClick={() => addToPrimary({
                                    ...creator,
                                    pricing: creator.pricing || {
                                      suggested_price: creator.creator_preferences_rate_per_reel || 10000
                                    }
                                  })}
                                >
                                  + Primary
                                </button>
                                <button 
                                  className="btn-add-backup"
                                  onClick={() => addToBackup({
                                    ...creator,
                                    pricing: creator.pricing || {
                                      suggested_price: creator.creator_preferences_rate_per_reel || 10000
                                    }
                                  })}
                                >
                                  + Backup
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                )}
              </div>

              {/* Right Panel - Selected Creators */}
              <div className="workspace-right">
                <div className="selection-panel">
                  <div className="selection-header">
                    <h2>📋 Selected Creators</h2>
                    <div className="budget-indicator">
                      <span className="budget-used">{formatCurrency(totalSelectedCost)}</span>
                      <span className="budget-separator">/</span>
                      <span className="budget-total">{formatCurrency(campaignData.max_budget)}</span>
                    </div>
                  </div>
                  
                  <div className="budget-bar">
                    <div 
                      className={`budget-fill ${budgetUsed > 100 ? 'over' : budgetUsed > 80 ? 'warning' : ''}`}
                      style={{ width: `${Math.min(budgetUsed, 100)}%` }}
                    ></div>
                  </div>

                  {/* Primary Creators */}
                  <div className="selection-section">
                    <div className="section-header">
                      <h3>🎯 Primary ({primaryCreators.length})</h3>
                      <span className="hint">Will receive invitations</span>
                    </div>
                    
                    {primaryCreators.length === 0 ? (
                      <div className="selection-empty">
                        <p>Add creators from AI results or CRM</p>
                      </div>
                    ) : (
                      <div className="selection-list">
                        {primaryCreators.map((creator, index) => (
                          <div key={creator.id} className="selected-item primary with-price">
                            <span className="item-order">{index + 1}</span>
                            <img 
                              src={creator.instagram_profile_picture || `https://ui-avatars.com/api/?name=${creator.name}`}
                              alt={creator.name}
                            />
                            <div className="item-info">
                              <span className="item-name">{creator.name}</span>
                              <span className="item-meta">{formatNumber(creator.instagram_followers_count)} followers</span>
                            </div>
                            <div className="item-price-edit">
                              <span className="price-label">₹</span>
                              <input
                                type="number"
                                className="price-input"
                                value={creator.adjusted_price || creator.pricing?.suggested_price || 100}
                                onChange={(e) => updateCreatorPrice(creator.id, e.target.value, true)}
                                onBlur={() => validateCreatorPrice(creator.id, true)}
                                min="1"
                                step="1"
                              />
                            </div>
                            <div className="item-actions">
                              <button 
                                className="btn-icon" 
                                onClick={() => moveToBackup(creator.id)}
                                title="Move to Backup"
                              >
                                ↓
                              </button>
                              <button 
                                className="btn-icon remove" 
                                onClick={() => removeFromPrimary(creator.id)}
                                title="Remove"
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Backup Creators */}
                  <div className="selection-section backup">
                    <div className="section-header">
                      <h3>🔄 Backup Queue ({backupCreators.length})</h3>
                      <span className="hint">Invited if primary declines</span>
                    </div>
                    
                    {backupCreators.length === 0 ? (
                      <div className="selection-empty">
                        <p>Add backup creators for safety</p>
                      </div>
                    ) : (
                      <div className="selection-list">
                        {backupCreators.map((creator, index) => (
                          <div key={creator.id} className="selected-item backup with-price">
                            <span className="item-order backup">{index + 1}</span>
                            <img 
                              src={creator.instagram_profile_picture || `https://ui-avatars.com/api/?name=${creator.name}`}
                              alt={creator.name}
                            />
                            <div className="item-info">
                              <span className="item-name">{creator.name}</span>
                              <span className="item-meta">{formatNumber(creator.instagram_followers_count)} followers</span>
                            </div>
                            <div className="item-price-edit">
                              <span className="price-label">₹</span>
                              <input
                                type="number"
                                className="price-input"
                                value={creator.adjusted_price || creator.pricing?.suggested_price || 100}
                                onChange={(e) => updateCreatorPrice(creator.id, e.target.value, false)}
                                onBlur={() => validateCreatorPrice(creator.id, false)}
                                min="1"
                                step="1"
                              />
                            </div>
                            <div className="item-actions">
                              <button 
                                className="btn-icon" 
                                onClick={() => moveBackupUp(index)}
                                disabled={index === 0}
                                title="Move Up"
                              >
                                ↑
                              </button>
                              <button 
                                className="btn-icon" 
                                onClick={() => moveBackupDown(index)}
                                disabled={index === backupCreators.length - 1}
                                title="Move Down"
                              >
                                ↓
                              </button>
                              <button 
                                className="btn-icon" 
                                onClick={() => moveToPrimary(creator.id)}
                                title="Move to Primary"
                              >
                                ⬆
                              </button>
                              <button 
                                className="btn-icon remove" 
                                onClick={() => removeFromBackup(creator.id)}
                                title="Remove"
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Summary */}
                  {projections && (
                    <div className="selection-summary">
                      <div className="summary-row">
                        <span>Est. Reach</span>
                        <span>{formatNumber(projections.estimated_reach)}</span>
                      </div>
                      <div className="summary-row">
                        <span>Avg. Engagement</span>
                        <span>{projections.avg_engagement_rate}%</span>
                      </div>
                      <div className="summary-row total">
                        <span>Total Cost</span>
                        <span>{formatCurrency(projections.total_cost)}</span>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="selection-actions">
                    <button 
                      className="btn-clear"
                      onClick={() => {
                        setPrimaryCreators([]);
                        setBackupCreators([]);
                      }}
                      disabled={primaryCreators.length === 0 && backupCreators.length === 0}
                    >
                      Clear All
                    </button>
                    <button 
                      className="btn-primary"
                      onClick={handleContinueToReview}
                      disabled={loading || primaryCreators.length === 0}
                    >
                      {loading ? 'Creating...' : 'Continue to Review →'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="workspace-footer">
              <button className="btn-secondary" onClick={() => setCurrentStep(1)}>
                ← Back to Details
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Review & Finalize */}
        {currentStep === 3 && (
          <div className="campaign-step step-3">
            <h1>Review & Finalize</h1>
            <p className="step-subtitle">Confirm your selections and block funds to launch the campaign</p>

            <div className="review-grid">
              {/* Campaign Summary */}
              <div className="review-card">
                <h3>Campaign Summary</h3>
                <div className="summary-list">
                  <div className="summary-row">
                    <span>Campaign Name</span>
                    <span>{campaignData.campaign_name}</span>
                  </div>
                  <div className="summary-row">
                    <span>Objective</span>
                    <span>{campaignData.objective}</span>
                  </div>
                  <div className="summary-row">
                    <span>Content Types</span>
                    <span>{campaignData.content_types.join(', ')}</span>
                  </div>
                  <div className="summary-row">
                    <span>Target Niches</span>
                    <span>{campaignData.niches.join(', ')}</span>
                  </div>
                  <div className="summary-row">
                    <span>Content Submission Deadline</span>
                    <span>{campaignData.delivery_days} days</span>
                  </div>
                  <div className="summary-row">
                    <span>Performance Monitoring Period</span>
                    <span>{campaignData.monitoring_weeks} week{campaignData.monitoring_weeks > 1 ? 's' : ''}</span>
                  </div>
                </div>
              </div>

              {/* Payment Breakdown */}
              <div className="review-card payment">
                <h3>Payment Breakdown</h3>
                {(() => {
                  const requiredCreators = primaryCreators.length;
                  const allCreators = [
                    ...primaryCreators.map(c => ({ 
                      ...c, 
                      price: c.adjusted_price || c.pricing?.suggested_price || 0,
                      type: 'primary'
                    })),
                    ...backupCreators.map(c => ({ 
                      ...c, 
                      price: c.adjusted_price || c.pricing?.suggested_price || 0,
                      type: 'backup'
                    }))
                  ];
                  const sortedByPrice = [...allCreators].sort((a, b) => b.price - a.price);
                  const topN = sortedByPrice.slice(0, requiredCreators);
                  const topNTotal = topN.reduce((sum, c) => sum + c.price, 0);
                  const primaryTotal = primaryCreators.reduce((sum, c) => sum + (c.adjusted_price || c.pricing?.suggested_price || 0), 0);
                  const platformFee = topNTotal * 0.035;
                  const totalToBlock = topNTotal + platformFee;
                  
                  return (
                    <>
                      <div className="payment-breakdown">
                        <div className="payment-row">
                          <span>Primary Creators Total</span>
                          <span>{formatCurrency(primaryTotal)}</span>
                        </div>
                        <div className="payment-row sub">
                          <span className="indent">↳ Fixed (60%)</span>
                          <span>{formatCurrency(primaryTotal * 0.6)}</span>
                        </div>
                        <div className="payment-row sub">
                          <span className="indent">↳ Performance (40%)</span>
                          <span>{formatCurrency(primaryTotal * 0.4)}</span>
                        </div>
                        <div className="payment-row escrow-row">
                          <span>🔒 Maximum Escrow (Top {requiredCreators})</span>
                          <span>{formatCurrency(topNTotal)}</span>
                        </div>
                        <div className="payment-row platform-fee">
                          <span>Platform Fee (3.5%)</span>
                          <span>{formatCurrency(platformFee)}</span>
                        </div>
                        <div className="payment-row total">
                          <span>💳 Amount to Block</span>
                          <span>{formatCurrency(totalToBlock)}</span>
                        </div>
                      </div>
                      
                      <div className="payment-info">
                        <h4>💡 How Payments Work</h4>
                        <ul>
                          <li><strong>Smart Escrow:</strong> We block based on top {requiredCreators} most expensive creators to cover any scenario</li>
                          <li><strong>Fixed (60%) + Performance (40%):</strong> Fixed released weekly during monitoring, performance at end</li>
                          <li><strong>Auto Refunds:</strong> If cheaper creators accept, or performance targets aren't met, difference is refunded</li>
                          <li><strong>Platform Fee (3.5%):</strong> One-time fee on actual creator payments</li>
                        </ul>
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Selected Creators Preview */}
              <div className="review-card creators">
                <h3>Primary Creators ({primaryCreators.length})</h3>
                <div className="creators-preview">
                  {primaryCreators.map(creator => (
                    <div key={creator.id} className="creator-preview-item">
                      <img 
                        src={creator.instagram_profile_picture || `https://ui-avatars.com/api/?name=${creator.name}`}
                        alt={creator.name}
                      />
                      <div>
                        <span className="name">{creator.name}</span>
                        <span className="price">{formatCurrency(creator.pricing?.suggested_price)}</span>
                      </div>
                    </div>
                  ))}
                </div>
                
                {backupCreators.length > 0 && (
                  <>
                    <h4 className="backup-header">Backup Queue ({backupCreators.length})</h4>
                    <div className="backup-preview">
                      {backupCreators.map((creator, idx) => (
                        <span key={creator.id} className="backup-item">
                          {idx + 1}. {creator.name}
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Projected Results */}
              <div className="review-card results">
                <h3>Projected Results</h3>
                <div className="results-grid">
                  <div className="result-item">
                    <span className="result-value">{formatNumber(projections?.estimated_reach)}</span>
                    <span className="result-label">Est. Reach</span>
                  </div>
                  <div className="result-item">
                    <span className="result-value">{formatNumber(projections?.estimated_engagement)}</span>
                    <span className="result-label">Est. Engagements</span>
                  </div>
                  <div className="result-item">
                    <span className="result-value">{projections?.avg_engagement_rate}%</span>
                    <span className="result-label">Avg. Engagement Rate</span>
                  </div>
                </div>
              </div>

              {/* Content Brief Preview */}
              <div className="review-card brief-preview">
                <h3>📝 Content Brief Preview</h3>
                <div className="brief-content">
                  {campaignData.content_brief && (
                    <div className="brief-section">
                      <h4>Campaign Brief</h4>
                      <p>{campaignData.content_brief}</p>
                    </div>
                  )}
                  {campaignData.content_script && (
                    <div className="brief-section">
                      <h4>Script / Talking Points</h4>
                      <p>{campaignData.content_script}</p>
                    </div>
                  )}
                  {campaignData.dos_and_donts && (
                    <div className="brief-section">
                      <h4>Do's and Don'ts</h4>
                      <pre>{campaignData.dos_and_donts}</pre>
                    </div>
                  )}
                  {campaignData.hashtags && (
                    <div className="brief-section">
                      <h4>Hashtags</h4>
                      <p className="hashtags">{campaignData.hashtags}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="escrow-notice">
              <span className="notice-icon">🔒</span>
              <div>
                <strong>Secure Escrow</strong>
                <p>Your funds will be held securely until campaign milestones are met. You maintain full control with our transparent release process.</p>
              </div>
            </div>

            {/* Pre-Launch Coming Soon Notice */}
            {IS_PRE_LAUNCH && (
              <div className="pre-launch-notice">
                <div className="pre-launch-header">
                  <span className="pre-launch-icon">🚀</span>
                  <h3>Launching Soon!</h3>
                </div>
                <div className="pre-launch-content">
                  <p>
                    Thank you for exploring Milan AI! We're currently in the final stages of setting up our 
                    <strong> secure payment infrastructure</strong> and <strong>creator verification process</strong>.
                  </p>
                  <div className="pre-launch-features">
                    <div className="feature-item">
                      <span>✅</span>
                      <span>AI-powered creator matching - Ready</span>
                    </div>
                    <div className="feature-item">
                      <span>✅</span>
                      <span>Campaign creation & management - Ready</span>
                    </div>
                    <div className="feature-item">
                      <span>✅</span>
                      <span>Performance tracking dashboard - Ready</span>
                    </div>
                    <div className="feature-item">
                      <span>🔄</span>
                      <span>Secure escrow payments - Coming Soon</span>
                    </div>
                    <div className="feature-item">
                      <span>🔄</span>
                      <span>Automated invoicing - Coming Soon</span>
                    </div>
                  </div>
                  <p className="pre-launch-cta">
                    <strong>Want early access?</strong> Your campaign details have been saved. 
                    We'll notify you as soon as we're ready to launch!
                  </p>
                  <div className="pre-launch-contact">
                    <p>Questions? Reach out to us at <a href="/contact">Contact Page</a></p>
                  </div>
                </div>
              </div>
            )}

            {/* Recovery banner: shown when Razorpay confirms a payment for
                this draft but our system never finalized (e.g. user
                refreshed during checkout). One-click finalize uses
                server-side state, so the brand never has to repay. */}
            {recoverableRazorpay && !IS_PRE_LAUNCH && (
              <div
                className="alert-banner warning"
                style={{
                  marginTop: 16,
                  padding: '14px 16px',
                  borderLeft: '4px solid #f59e0b',
                  background: '#fffbeb',
                  borderRadius: 8,
                  display: 'flex',
                  gap: 12,
                  alignItems: 'flex-start',
                }}
              >
                <span style={{ fontSize: 22 }}>💳</span>
                <div style={{ flex: 1 }}>
                  <h4 style={{ margin: '0 0 6px 0' }}>Payment received — campaign not finalized</h4>
                  <p style={{ margin: 0 }}>
                    We received your payment of{' '}
                    <strong>{formatCurrency(recoverableRazorpay.amount || 0)}</strong>, but the
                    browser closed before invitations were sent. Click below to send invitations
                    now — no need to pay again.
                  </p>
                  {recoverableRazorpay.error && (
                    <p style={{ color: '#dc2626', marginTop: 6, fontSize: 13 }}>
                      {recoverableRazorpay.error}
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="step-actions three-buttons">
              <button className="btn-secondary" onClick={() => setCurrentStep(2)}>
                ← Back to Selection
              </button>
              <button 
                className="btn-danger"
                onClick={() => setShowDeleteModal(true)}
                disabled={loading || !!recoverableRazorpay?.finalizing}
              >
                🗑️ Delete Campaign
              </button>
              {IS_PRE_LAUNCH ? (
                <button 
                  className="btn-primary finalize coming-soon" 
                  onClick={() => navigate('/brand-dashboard')}
                >
                  💾 Save & Return to Dashboard
                </button>
              ) : recoverableRazorpay ? (
                <button
                  className="btn-primary finalize"
                  onClick={handleResumeFinalize}
                  disabled={!!recoverableRazorpay.finalizing}
                >
                  {recoverableRazorpay.finalizing
                    ? 'Sending invitations…'
                    : '✅ Send invitations now (payment received)'}
                </button>
              ) : (
                <button 
                  className="btn-primary finalize" 
                  onClick={handleShowPayment}
                  disabled={loading}
                >
                  {loading ? 'Processing...' : '💳 Pay & Send Invitations'}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="modal-overlay">
            <div className="confirmation-modal delete-modal">
              <div className="modal-header">
                <h2>🗑️ Delete Campaign</h2>
              </div>
              
              <div className="modal-body">
                <div className="delete-warning">
                  <div className="warning-icon">⚠️</div>
                  <p>Are you sure you want to delete this campaign?</p>
                </div>

                <div className="delete-details">
                  <p><strong>Campaign:</strong> {campaignData.campaign_name}</p>
                  <p><strong>Selected Creators:</strong> {primaryCreators.length} primary, {backupCreators.length} backup</p>
                </div>

                <p className="delete-note">This action cannot be undone. The campaign and all its data will be permanently deleted.</p>
              </div>

              <div className="modal-actions">
                <button 
                  className="btn-secondary"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button 
                  className="btn-danger"
                  onClick={handleDeleteCampaign}
                  disabled={loading}
                >
                  {loading ? 'Deleting...' : '🗑️ Yes, Delete Campaign'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Price Edit Modal */}
        {showPriceEditModal && (
          <div className="modal-overlay">
            <div className="price-edit-modal">
              <div className="modal-header">
                <h2>💰 Set Creator Prices</h2>
                <p className="modal-subtitle">Adjust prices for each creator before proceeding</p>
              </div>
              
              <div className="modal-body">
                <div className="price-edit-info">
                  <span className="info-icon">ℹ️</span>
                  <p>
                    Enter any whole-rupee amount (minimum <strong>₹1</strong>). The platform still enforces{' '}
                    <strong>-20% to +50%</strong> around the recommended price when you pay. Lower prices may reduce acceptance rates.
                  </p>
                </div>

                <div className="price-edit-list">
                  <div className="price-edit-section">
                    <h3>🎯 Primary Creators ({primaryCreators.length})</h3>
                    {primaryCreators.map((creator, index) => (
                      <div key={creator.id} className="price-edit-item">
                        <div className="creator-details">
                          <img 
                            src={creator.instagram_profile_picture || `https://ui-avatars.com/api/?name=${creator.name}`}
                            alt={creator.name}
                          />
                          <div className="creator-info">
                            <span className="creator-name">{creator.name}</span>
                            <span className="creator-meta">
                              @{creator.instagram_username || 'username'} • {formatNumber(creator.instagram_followers_count)} followers
                            </span>
                          </div>
                        </div>
                        <div className="price-edit-controls">
                          <div className="recommended-price">
                            <span className="label">Recommended</span>
                            <span className="value">{formatCurrency(creator.pricing?.original_price || creator.pricing?.suggested_price || 0)}</span>
                          </div>
                          <div className="your-price">
                            <span className="label">Your Price</span>
                            <div className="price-input-group">
                              <span className="currency">₹</span>
                              <input
                                type="number"
                                value={creator.adjusted_price || creator.pricing?.suggested_price || 100}
                                onChange={(e) => updateCreatorPrice(creator.id, e.target.value, true)}
                                onBlur={() => validateCreatorPrice(creator.id, true)}
                                min="1"
                                step="1"
                                placeholder="Amount in ₹"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {backupCreators.length > 0 && (
                    <div className="price-edit-section backup">
                      <h3>🔄 Backup Creators ({backupCreators.length})</h3>
                      {backupCreators.map((creator, index) => (
                        <div key={creator.id} className="price-edit-item">
                          <div className="creator-details">
                            <img 
                              src={creator.instagram_profile_picture || `https://ui-avatars.com/api/?name=${creator.name}`}
                              alt={creator.name}
                            />
                            <div className="creator-info">
                              <span className="creator-name">{creator.name}</span>
                              <span className="creator-meta">
                                @{creator.instagram_username || 'username'} • {formatNumber(creator.instagram_followers_count)} followers
                              </span>
                            </div>
                          </div>
                          <div className="price-edit-controls">
                            <div className="recommended-price">
                              <span className="label">Recommended</span>
                              <span className="value">{formatCurrency(creator.pricing?.original_price || creator.pricing?.suggested_price || 100)}</span>
                            </div>
                            <div className="your-price">
                              <span className="label">Your Price</span>
                              <div className="price-input-group">
                                <span className="currency">₹</span>
                                <input
                                  type="number"
                                  value={creator.adjusted_price || creator.pricing?.suggested_price || 100}
                                  onChange={(e) => updateCreatorPrice(creator.id, e.target.value, false)}
                                  onBlur={() => validateCreatorPrice(creator.id, false)}
                                  min="1"
                                  step="1"
                                  placeholder="Amount in ₹"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Smart Escrow Calculation - Top N by Price */}
                {(() => {
                  const requiredCreators = primaryCreators.length;
                  const allCreators = [
                    ...primaryCreators.map(c => ({ 
                      ...c, 
                      price: c.adjusted_price || c.pricing?.suggested_price || 0,
                      type: 'primary'
                    })),
                    ...backupCreators.map(c => ({ 
                      ...c, 
                      price: c.adjusted_price || c.pricing?.suggested_price || 0,
                      type: 'backup'
                    }))
                  ];
                  
                  // Sort by price descending and take top N
                  const sortedByPrice = [...allCreators].sort((a, b) => b.price - a.price);
                  const topN = sortedByPrice.slice(0, requiredCreators);
                  const topNTotal = topN.reduce((sum, c) => sum + c.price, 0);
                  const primaryTotal = primaryCreators.reduce((sum, c) => sum + (c.adjusted_price || c.pricing?.suggested_price || 0), 0);
                  const platformFee = topNTotal * 0.035;
                  const totalToBlock = topNTotal + platformFee;
                  
                  return (
                    <>
                      <div className="escrow-explanation">
                        <div className="escrow-header">
                          <span className="escrow-icon">🔒</span>
                          <h4>Smart Escrow Calculation</h4>
                        </div>
                        <p className="escrow-desc">
                          To ensure we can always fulfill your campaign (even if some creators decline), 
                          we calculate the escrow based on the <strong>top {requiredCreators} most expensive creators</strong> from your selection.
                        </p>
                        
                        <div className="top-n-breakdown">
                          <div className="breakdown-header">
                            <span>Top {requiredCreators} Creators by Price:</span>
                          </div>
                          <div className="breakdown-list">
                            {topN.map((creator, idx) => (
                              <div key={creator.id} className={`breakdown-item ${creator.type}`}>
                                <span className="breakdown-rank">#{idx + 1}</span>
                                <span className="breakdown-name">{creator.name}</span>
                                <span className={`breakdown-badge ${creator.type}`}>
                                  {creator.type === 'primary' ? '🎯 Primary' : '🔄 Backup'}
                                </span>
                                <span className="breakdown-price">{formatCurrency(creator.price)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="price-edit-summary">
                        <div className="summary-row">
                          <span>Primary Creators Total</span>
                          <span>{formatCurrency(primaryTotal)}</span>
                        </div>
                        <div className="summary-row highlight">
                          <span>Maximum Escrow (Top {requiredCreators})</span>
                          <span>{formatCurrency(topNTotal)}</span>
                        </div>
                        <div className="summary-row platform-fee-row">
                          <span>+ Platform Fee (3.5%)</span>
                          <span>{formatCurrency(platformFee)}</span>
                        </div>
                        <div className="summary-row total-row">
                          <span>💳 Amount to Block</span>
                          <span className="total-amount">{formatCurrency(totalToBlock)}</span>
                        </div>
                      </div>

                      <div className="escrow-note">
                        <span className="note-icon">💡</span>
                        <p>
                          <strong>How refunds work:</strong> When creators accept, we only charge their actual price. 
                          If a ₹1,500 slot is filled by a ₹1,000 creator, ₹500 is automatically refunded to your account.
                        </p>
                      </div>
                    </>
                  );
                })()}

                {/* Backup Creator Validation Warning */}
                {backupCreators.length < Math.ceil(primaryCreators.length * 0.5) && (
                  <div className="backup-warning">
                    <span className="warning-icon">⚠️</span>
                    <p>
                      You need at least <strong>{Math.ceil(primaryCreators.length * 0.5)} backup creator(s)</strong> (50% of primary). 
                      Currently selected: {backupCreators.length}
                    </p>
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button 
                  className="btn-secondary"
                  onClick={() => setShowPriceEditModal(false)}
                >
                  ← Back to Selection
                </button>
                <button 
                  className="btn-primary"
                  onClick={handleCreateCampaign}
                  disabled={loading || backupCreators.length < Math.ceil(primaryCreators.length * 0.5)}
                >
                  {loading ? 'Creating...' : 'Confirm Prices & Continue →'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Success */}
        {currentStep === 4 && (
          <div className="campaign-step step-4">
            <div className="success-content">
              <div className="success-icon">🎉</div>
              <h1>Campaign Launched Successfully!</h1>
              <p>Invitations have been sent to all selected creators.</p>

              <div className="next-steps">
                <h3>What Happens Next?</h3>
                <div className="steps-timeline">
                  <div className="timeline-item">
                    <span className="timeline-icon">📧</span>
                    <div>
                      <strong>Creators Receive Invitations</strong>
                      <p>Primary creators have 3 days to accept. Backups invited in order if needed.</p>
                    </div>
                  </div>
                  <div className="timeline-item">
                    <span className="timeline-icon">🎬</span>
                    <div>
                      <strong>Content Creation</strong>
                      <p>Accepted creators will submit content for your review</p>
                    </div>
                  </div>
                  <div className="timeline-item">
                    <span className="timeline-icon">✅</span>
                    <div>
                      <strong>Review & Approve</strong>
                      <p>Review and approve submitted content (up to 3 revisions)</p>
                    </div>
                  </div>
                  <div className="timeline-item">
                    <span className="timeline-icon">📊</span>
                    <div>
                      <strong>Performance Monitoring & Payment</strong>
                      <p>Fixed (60%) released weekly during monitoring, performance (40%) after completion</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="success-actions">
                <button className="btn-secondary" onClick={() => navigate('/brand-dashboard')}>
                  Go to Dashboard
                </button>
                <button className="btn-primary" onClick={() => navigate(`/brand-dashboard?tab=live&campaign=${campaignId}`)}>
                  View Campaign Details
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        campaignId={campaignId}
        creators={primaryCreators}
        backupCreators={backupCreators}
        onPaymentSuccess={handlePaymentSuccess}
        brandEmail={localStorage.getItem('userEmail')}
        brandName={localStorage.getItem('userName')}
      />

      <Footer />
    </div>
  );
}

export default CreateCampaign;
